import { createAnimation } from '@ionic/react';
import { useEffect, useRef, createElement } from 'react';


const useNavigator = ({ views, currentView, setView, setTab }) => {

  const animationRef = useRef();
  const currentViewRef = useRef(currentView);

  const screenKey = currentView.view
    ? currentView.view + ':' + (currentView.payload?.solution?.id || currentView.payload?.challenge?.id || '')
    : '';

  const Screen = currentView.view ? createElement(views[currentView.view], { ...(currentView?.payload || {}), ref: animationRef, key: screenKey }) : '';

  // Keep ref in sync so the popstate handler always reads the latest view
  // without needing to be re-registered on every navigation.
  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  // Popstate listener — registered once, stable across navigations.
  useEffect(() => {
    const handlePopstate = (event) => {
      const state = event?.state;
      if (state?.view) {
        setView({ view: state.view, payload: state.payload, tab: state.tab });
      } else {
        // Reached the state-less initial browser history entry.
        // Seed it with the current view so subsequent back/forward stays consistent.
        const cv = currentViewRef.current;
        window.history.replaceState({ view: cv.view, payload: cv.payload, tab: cv.tab }, null);
      }
    };

    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [setView]);

  // Sync active tab whenever the view changes.
  useEffect(() => {
    currentView?.tab && setTab(currentView.tab);
  }, [currentView?.tab, setTab]);

  useEffect(() => {
    if (!animationRef.current) return;
    const animation = createAnimation()
      .addElement(animationRef.current)
      .duration(600)
      .fromTo('opacity', '0.2', '1')
      .fromTo('transform', 'scale(80%)', 'scale(100%)')
      .easing('cubic-bezier(.66,.28,0,.64)');

    animation.play();
  }, [screenKey]);


  return Screen;
};

export default useNavigator;
